import React, { ElementType } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Plus, Search, LayoutGrid, Rows3, SlidersHorizontal } from 'lucide-react';

export const leadAccent = '#006400';
export const leadAccentSoft = '#E6F4EA';
export const leadBorder = 'gray.200';

export const LeadShell = ({
  children,
  compact = false,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <Box
    px={{ base: 4, md: 6, xl: 8 }}
    py={{ base: 4, md: 6 }}
    bg="linear-gradient(180deg, #f6faf7 0%, #ffffff 32%)"
    minH="calc(100vh - 72px)"
  >
    <Box maxW={compact ? '1120px' : '1360px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const LeadHero = ({
  title,
  description,
  primaryAction,
  secondaryAction,
}: {
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={leadBorder}
    borderRadius={{ base: '24px', md: '28px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 7 }}
    boxShadow="0 18px 48px rgba(15, 23, 42, 0.06)"
    mb={6}
  >
    <Flex
      align={{ base: 'stretch', lg: 'center' }}
      justify="space-between"
      direction={{ base: 'column', lg: 'row' }}
      gap={5}
    >
      <VStack align="flex-start" spacing={3} maxW="3xl">
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="0.18em"
          fontWeight="700"
          color={leadAccent}
        >
          Leads
        </Text>
        <Text
          as="h1"
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="800"
          color="gray.900"
          lineHeight={{ base: '1.2', md: '1.05' }}
          letterSpacing="-0.03em"
        >
          {title}
        </Text>
        <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" maxW="2xl">
          {description}
        </Text>
      </VStack>
      {(primaryAction || secondaryAction) && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
          {primaryAction}
          {secondaryAction}
        </Stack>
      )}
    </Flex>
  </Box>
);

export const LeadPrimaryButton = ({
  children,
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button
    bg={leadAccent}
    color="white"
    leftIcon={props.leftIcon}
    _hover={{ bg: '#004d00', transform: 'translateY(-1px)' }}
    _active={{ bg: '#003300' }}
    boxShadow="0 12px 26px rgba(0, 100, 0, 0.18)"
    borderRadius="16px"
    fontWeight="700"
    px={5}
    {...props}
  >
    {children}
  </Button>
);

export const LeadSectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={leadBorder}
    borderRadius="24px"
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Flex
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap={3}
      mb={5}
    >
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export const LeadMetricStrip = ({
  items,
}: {
  items: Array<{ label: string; value: string; hint?: string; onClick?: () => void; active?: boolean }>;
}) => (
  <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
    {items.map((item) => (
      <Box
        key={item.label}
        bg={item.active ? leadAccentSoft : 'white'}
        borderWidth="1px"
        borderColor={item.active ? 'green.200' : leadBorder}
        borderRadius="22px"
        px={4}
        py={4}
        cursor={item.onClick ? 'pointer' : 'default'}
        onClick={item.onClick}
        transition="all 0.16s ease"
        _hover={
          item.onClick
            ? {
                borderColor: 'green.300',
                transform: 'translateY(-1px)',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
              }
            : undefined
        }
      >
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
          {item.label}
        </Text>
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" color="gray.900" mt={2}>
          {item.value}
        </Text>
        {item.hint ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {item.hint}
          </Text>
        ) : null}
      </Box>
    ))}
  </SimpleGrid>
);

export const LeadCommandBar = ({
  search,
  onSearchChange,
  inputRef,
  stage,
  onStageChange,
  dueOnly,
  onDueOnlyChange,
  sortKey,
  onSortKeyChange,
  sortDir,
  onSortDirToggle,
  view,
  onViewChange,
  resultText,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  stage: string;
  onStageChange: (value: string) => void;
  dueOnly: boolean;
  onDueOnlyChange: (value: boolean) => void;
  sortKey: string;
  onSortKeyChange: (value: string) => void;
  sortDir: 'asc' | 'desc';
  onSortDirToggle: () => void;
  view: 'table' | 'cards';
  onViewChange: (value: 'table' | 'cards') => void;
  resultText: string;
}) => (
  <Box
    bg="rgba(255, 255, 255, 0.92)"
    borderWidth="1px"
    borderColor={leadBorder}
    backdropFilter="blur(12px)"
    borderRadius="24px"
    px={{ base: 4, md: 5 }}
    py={{ base: 4, md: 4 }}
    position="sticky"
    top={{ base: 70, md: 78 }}
    zIndex={5}
    boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
    mb={6}
  >
    <Stack spacing={4}>
      <Flex
        gap={3}
        wrap="wrap"
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
      >
        <InputGroup maxW={{ base: 'full', lg: '420px' }}>
          <InputLeftElement pointerEvents="none">
            <Search size={16} color="#718096" />
          </InputLeftElement>
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name, email, or phone"
            borderRadius="16px"
            bg="gray.50"
            borderColor="gray.100"
          />
        </InputGroup>
        <HStack spacing={2} flexWrap="wrap">
          <Select
            value={stage}
            onChange={(e) => onStageChange(e.target.value)}
            maxW={{ base: 'full', sm: '180px' }}
            bg="gray.50"
            borderRadius="16px"
            borderColor="gray.100"
          >
            <option value="">All stages</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="proposal">Proposal</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </Select>
          <Button
            variant={dueOnly ? 'solid' : 'outline'}
            bg={dueOnly ? leadAccent : 'white'}
            color={dueOnly ? 'white' : 'gray.700'}
            borderRadius="16px"
            onClick={() => onDueOnlyChange(!dueOnly)}
            leftIcon={<SlidersHorizontal size={16} />}
          >
            Due only
          </Button>
          <Select
            value={sortKey}
            onChange={(e) => onSortKeyChange(e.target.value)}
            maxW={{ base: 'full', sm: '200px' }}
            bg="gray.50"
            borderRadius="16px"
            borderColor="gray.100"
          >
            <option value="created_at">Date created</option>
            <option value="first_name">First name</option>
            <option value="stage">Stage</option>
            <option value="next_follow_up">Next follow-up</option>
          </Select>
          <Button borderRadius="16px" variant="outline" onClick={onSortDirToggle}>
            {sortDir === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
          <HStack
            spacing={1}
            p={1}
            borderWidth="1px"
            borderColor="gray.100"
            borderRadius="16px"
            bg="gray.50"
          >
            <IconButton
              aria-label="Table view"
              icon={<Box as={Rows3 as ElementType} />}
              size="sm"
              variant={view === 'table' ? 'solid' : 'ghost'}
              bg={view === 'table' ? leadAccent : 'transparent'}
              color={view === 'table' ? 'white' : 'gray.600'}
              _hover={{ bg: view === 'table' ? '#004d00' : 'gray.100' }}
              onClick={() => onViewChange('table')}
            />
            <IconButton
              aria-label="Card view"
              icon={<Box as={LayoutGrid as ElementType} />}
              size="sm"
              variant={view === 'cards' ? 'solid' : 'ghost'}
              bg={view === 'cards' ? leadAccent : 'transparent'}
              color={view === 'cards' ? 'white' : 'gray.600'}
              _hover={{ bg: view === 'cards' ? '#004d00' : 'gray.100' }}
              onClick={() => onViewChange('cards')}
            />
          </HStack>
        </HStack>
      </Flex>
      <Text fontSize="sm" color="gray.500">
        {resultText}
      </Text>
    </Stack>
  </Box>
);

export const LeadStageBadge = ({ stage }: { stage?: string }) => {
  const colorMap: Record<string, string> = {
    new: 'gray',
    contacted: 'blue',
    qualified: 'orange',
    proposal: 'purple',
    won: 'green',
    lost: 'red',
  };

  return (
    <Badge
      colorScheme={colorMap[stage || ''] || 'gray'}
      borderRadius="full"
      px={3}
      py={1}
      textTransform="capitalize"
    >
      {stage || 'Unknown'}
    </Badge>
  );
};

export const LeadInterestBadge = ({ label }: { label?: string }) => {
  const colorMap: Record<string, string> = {
    'Highly Interested': 'red',
    'Medium Interest': 'orange',
    Interested: 'green',
    Neutral: 'gray',
    'Not Interested': 'gray',
  };

  if (!label) {
    return null;
  }

  return (
    <Badge colorScheme={colorMap[label] || 'gray'} borderRadius="full" px={3} py={1}>
      {label}
    </Badge>
  );
};

export const LeadEmptyState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack
    bg="white"
    borderWidth="1px"
    borderColor={leadBorder}
    borderRadius="28px"
    px={{ base: 6, md: 10 }}
    py={{ base: 10, md: 14 }}
    spacing={4}
    textAlign="center"
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Box
      w="64px"
      h="64px"
      borderRadius="20px"
      bg={leadAccentSoft}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box as={Plus as ElementType} boxSize={7} color={leadAccent} />
    </Box>
    <VStack spacing={2}>
      <Text fontSize="xl" fontWeight="800" color="gray.900">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500" maxW="md">
        {description}
      </Text>
    </VStack>
    {action}
  </VStack>
);

export const LeadDashboardSkeleton = () => (
  <Stack spacing={4}>
    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4}>
      {[...Array(4)].map((_, idx) => (
        <Box key={idx} bg="white" borderWidth="1px" borderColor={leadBorder} borderRadius="22px" px={4} py={4}>
          <Skeleton h="12px" w="40%" mb={3} />
          <Skeleton h="24px" w="55%" mb={2} />
          <Skeleton h="12px" w="70%" />
        </Box>
      ))}
    </SimpleGrid>
    {[...Array(5)].map((_, idx) => (
      <Box key={idx} bg="white" borderWidth="1px" borderColor={leadBorder} borderRadius="22px" px={5} py={4}>
        <Skeleton h="18px" w="30%" mb={3} />
        <SkeletonText noOfLines={2} spacing="3" />
      </Box>
    ))}
  </Stack>
);
